import { useEffect, useRef, useState } from "react";
import { fetchAdminUser, fetchAdminUsers, updateAdminUserCollectorTier, updateAdminUserRole, updateAdminUserStatus } from "./api/admin";
import { useAppDialog } from "./components/AppDialogProvider";
import CollectorTierBadge from "./components/CollectorTierBadge";
import TraderModal from "./features/traders/components/TraderModal";
import UserIdentityLabel from "./components/UserIdentityLabel";
import { useAuth } from "./authContext";
import { buildCollectorTierOptions, formatCollectorTierLimit, getCollectorTierDefinition } from "./utils/collectorTiers";
import usePaginationScroll from "./utils/usePaginationScroll";

const STATUS_FILTERS = [
  { value: "all", label: "All users" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "pending_verification", label: "Pending" }
];

const ROLE_FILTERS = [
  { value: "all", label: "All roles" },
  { value: "admin", label: "Admin" },
  { value: "moderator", label: "Moderator" },
  { value: "collector", label: "Collector" }
];

const DEFAULT_SORT = {
  key: "lastSeenAt",
  direction: "desc"
};

const COLLECTOR_TIER_OPTIONS = buildCollectorTierOptions();

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 25,
  totalItems: 0,
  totalPages: 1,
  hasNextPage: false,
  hasPreviousPage: false
};

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis-right", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [1, "ellipsis-left", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis-left", currentPage - 1, currentPage, currentPage + 1, "ellipsis-right", totalPages];
}

function formatLastSeen(value) {
  if (!value) {
    return "Never";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

function getTradeFeedbackDisplay(value) {
  const numericValue = Number(value);

  if (numericValue === 3) {
    return { emoji: "😀", label: "Good" };
  }

  if (numericValue === 2) {
    return { emoji: "😐", label: "Neutral" };
  }

  if (numericValue === 1) {
    return { emoji: "🙁", label: "Bad" };
  }

  return { emoji: "❔", label: "Unknown" };
}

function summarizeTradeFeedback(entries = []) {
  return entries.reduce((summary, entry) => {
    const rating = Number(entry?.rating);

    if (rating === 3) {
      summary.happy += 1;
    } else if (rating === 2) {
      summary.neutral += 1;
    } else if (rating === 1) {
      summary.unhappy += 1;
    }

    return summary;
  }, {
    happy: 0,
    neutral: 0,
    unhappy: 0
  });
}

function formatDateTime(value) {
  if (!value) {
    return "Not set";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

function formatAcceptance(value, version) {
  if (!value) {
    return "Not accepted";
  }

  const formattedDate = formatDateTime(value);

  if (!version) {
    return formattedDate;
  }

  return `${formattedDate} (${version})`;
}

function TradeFeedbackLogModal({ isOpen, onClose, user, entries }) {
  return (
    <TraderModal
      isOpen={isOpen}
      onClose={onClose}
      title={user?.username ? `Trade review log · ${user.username}` : "Trade review log"}
      width="1120px"
    >
      <div style={{ display: "grid", gap: "16px" }}>
        <p style={{ margin: 0, color: "#475569", lineHeight: 1.5 }}>
          All received trade reviews for this user, including card counts from the completed deal revision.
        </p>
        <div className="admin-users-trade-log-shell">
          <table className="admin-users-trade-log-table">
            <thead>
              <tr>
                <th>Trade #</th>
                <th>Counterparty</th>
                <th>Review</th>
                <th>My Traders</th>
                <th>My Wants</th>
                <th>Completed</th>
                <th>Wants cleanup</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {entries.length > 0 ? entries.map((entry) => {
                const rating = getTradeFeedbackDisplay(entry.rating);

                return (
                  <tr key={`${entry.dealId}-${entry.reviewSubmittedAt || entry.completedAt || "feedback"}`}>
                    <td>{entry.dealId || "-"}</td>
                    <td>
                      <div className="admin-users-trade-log-counterparty">
                        <strong>{entry.counterparty?.username || "Unknown user"}</strong>
                      </div>
                    </td>
                    <td>
                      <span className="admin-users-feedback-emoji" role="img" aria-label={rating.label} title={rating.label}>
                        {rating.emoji}
                      </span>
                    </td>
                    <td>{Number(entry.myTradersCardCount ?? 0)}</td>
                    <td>{Number(entry.myWantsCardCount ?? 0)}</td>
                    <td>{formatDateTime(entry.reviewSubmittedAt || entry.completedAt)}</td>
                    <td>{entry.removeMatchedWants ? "Removed" : "Kept"}</td>
                    <td className="admin-users-trade-log-remarks">{entry.remarks || "No remarks"}</td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="8">
                    <div className="admin-users-empty">No trade feedback recorded for this user yet.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </TraderModal>
  );
}

export default function AdminUsersView() {
  const { user: currentUser } = useAuth();
  const { showConfirm, showPrompt } = useAppDialog();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [pageInput, setPageInput] = useState(String(DEFAULT_PAGINATION.page));
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserDetail, setSelectedUserDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [noticeMessage, setNoticeMessage] = useState("");
  const [pendingUserId, setPendingUserId] = useState(null);
  const [pendingRoleUserId, setPendingRoleUserId] = useState(null);
  const [pendingCollectorTierUserId, setPendingCollectorTierUserId] = useState(null);
  const [isTradeFeedbackModalOpen, setIsTradeFeedbackModalOpen] = useState(false);
  const hasLoadedUsersRef = useRef(false);

  const canEditRoles = currentUser?.role === "admin";

  useEffect(() => {
    let isCancelled = false;

    const loadUsers = async () => {
      const showLoading = !hasLoadedUsersRef.current && pagination.page === DEFAULT_PAGINATION.page;

      if (showLoading) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }

      try {
        const payload = await fetchAdminUsers({
          search,
          status: statusFilter === "all" ? "" : statusFilter,
          role: roleFilter === "all" ? "" : roleFilter,
          page: pagination.page,
          pageSize: pagination.pageSize,
          sort: sortConfig.key,
          direction: sortConfig.direction
        });

        if (isCancelled) {
          return;
        }

        const nextUsers = Array.isArray(payload?.users) ? payload.users : [];

        setUsers(nextUsers);
        setPagination((currentPagination) => ({
          ...currentPagination,
          ...DEFAULT_PAGINATION,
          ...(payload?.pagination || {}),
          page: Number(payload?.pagination?.page || currentPagination.page),
          pageSize: Number(payload?.pagination?.pageSize || currentPagination.pageSize),
          totalItems: Number(payload?.pagination?.totalItems || 0),
          totalPages: Number(payload?.pagination?.totalPages || 1),
          hasNextPage: Boolean(payload?.pagination?.hasNextPage),
          hasPreviousPage: Boolean(payload?.pagination?.hasPreviousPage)
        }));
        setSelectedUserId((currentSelectedUserId) => {
          if (nextUsers.some((entry) => Number(entry.id) === Number(currentSelectedUserId))) {
            return currentSelectedUserId;
          }

          return nextUsers[0]?.id ?? null;
        });
        setErrorMessage("");
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage(error.message || "Could not load users.");
        }
      } finally {
        if (!isCancelled) {
          hasLoadedUsersRef.current = true;
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    };

    loadUsers();

    return () => {
      isCancelled = true;
    };
  }, [search, statusFilter, roleFilter, sortConfig, pagination.page, pagination.pageSize]);

  useEffect(() => {
    if (!selectedUserId) {
      setSelectedUserDetail(null);
      return undefined;
    }

    let isCancelled = false;

    const loadDetail = async () => {
      setIsDetailLoading(true);

      try {
        const payload = await fetchAdminUser(selectedUserId);

        if (!isCancelled) {
          setSelectedUserDetail(payload || null);
        }
      } catch (error) {
        if (!isCancelled) {
          setSelectedUserDetail(null);
          setErrorMessage(error.message || "Could not load user detail.");
        }
      } finally {
        if (!isCancelled) {
          setIsDetailLoading(false);
        }
      }
    };

    loadDetail();

    return () => {
      isCancelled = true;
    };
  }, [selectedUserId]);

  useEffect(() => {
    setIsTradeFeedbackModalOpen(false);
  }, [selectedUserId]);

  useEffect(() => {
    setPageInput(String(pagination.page));
  }, [pagination.page]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPagination((currentPagination) => ({ ...currentPagination, page: 1 }));
    setSearch(searchInput.trim());
  };

  const handleStatusFilterChange = (nextStatusFilter) => {
    setPagination((currentPagination) => ({ ...currentPagination, page: 1 }));
    setStatusFilter(nextStatusFilter);
  };

  const handleRoleFilterChange = (nextRoleFilter) => {
    setPagination((currentPagination) => ({ ...currentPagination, page: 1 }));
    setRoleFilter(nextRoleFilter);
  };

  const handleSortChange = (key) => {
    setPagination((currentPagination) => ({ ...currentPagination, page: 1 }));
    setSortConfig((currentSort) => {
      if (currentSort.key === key) {
        return {
          key,
          direction: currentSort.direction === "asc" ? "desc" : "asc"
        };
      }

      return {
        key,
        direction: key === "username" || key === "role" || key === "status" ? "asc" : "desc"
      };
    });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) {
      return "";
    }

    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > pagination.totalPages || nextPage === pagination.page) {
      return;
    }

    setPagination((currentPagination) => ({ ...currentPagination, page: nextPage }));
  };
  const tableSectionRef = usePaginationScroll(pagination.page);

  const commitPageInput = () => {
    const nextPage = Number.parseInt(pageInput, 10);

    if (!Number.isFinite(nextPage)) {
      setPageInput(String(pagination.page));
      return;
    }

    const clampedPage = Math.min(Math.max(nextPage, 1), pagination.totalPages);
    setPageInput(String(clampedPage));

    if (clampedPage !== pagination.page) {
      handlePageChange(clampedPage);
    }
  };

  const handlePageSizeChange = (event) => {
    const nextPageSize = Number.parseInt(event.target.value, 10) || DEFAULT_PAGINATION.pageSize;

    setPagination((currentPagination) => ({
      ...currentPagination,
      page: 1,
      pageSize: nextPageSize
    }));
  };

  const refreshDetail = async (userId) => {
    if (!userId) {
      return;
    }

    const detailPayload = await fetchAdminUser(userId);
    setSelectedUserDetail(detailPayload || null);
  };

  const handleStatusToggle = async (user) => {
    const nextStatus = user.status === "suspended" ? "active" : "suspended";
    const suspensionPromptResult = nextStatus === "suspended"
      ? await showPrompt({
          title: "Suspend User",
          message: `Reason for suspending ${user.username}?`,
          inputLabel: "Suspension reason",
          confirmLabel: "Suspend user",
          tone: "danger"
        })
      : "";

    if (nextStatus === "suspended" && suspensionPromptResult === null) {
      return;
    }

    const suspensionReason = nextStatus === "suspended"
      ? suspensionPromptResult
      : "";

    setPendingUserId(user.id);
    setNoticeMessage("");

    try {
      const payload = await updateAdminUserStatus(user.id, { status: nextStatus, suspensionReason });
      const updatedUser = payload?.user;

      setUsers((currentUsers) => currentUsers.map((entry) => (
        Number(entry.id) === Number(updatedUser?.id)
          ? { ...entry, ...updatedUser }
          : entry
      )));

      setNoticeMessage(
        nextStatus === "suspended"
          ? `${user.username} has been suspended.`
          : `${user.username} has been reactivated.`
      );
      if (Number(selectedUserId) === Number(updatedUser?.id)) {
        await refreshDetail(updatedUser.id);
      }
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.message || "Could not update user status.");
    } finally {
      setPendingUserId(null);
    }
  };

  const handleRoleChange = async (targetUser, nextRole) => {
    if (!nextRole || nextRole === targetUser.role) {
      return;
    }

    const roleChangeConfirmed = await showConfirm({
      title: "Change User Role",
      message: `Change ${targetUser.username} from ${targetUser.role} to ${nextRole}?`,
      confirmLabel: "Change role"
    });

    if (!roleChangeConfirmed) {
      return;
    }

    if (nextRole === "admin") {
      const adminPromotionConfirmation = await showPrompt({
        title: "Confirm Admin Promotion",
        message: `Type ADMIN to confirm promoting ${targetUser.username} to admin.`,
        inputLabel: "Confirmation",
        placeholder: "ADMIN",
        confirmLabel: "Promote user",
        tone: "danger"
      });

      if (adminPromotionConfirmation !== "ADMIN") {
        return;
      }
    }

    setPendingRoleUserId(targetUser.id);
    setNoticeMessage("");

    try {
      const payload = await updateAdminUserRole(targetUser.id, nextRole);
      const updatedUser = payload?.user;

      setUsers((currentUsers) => currentUsers.map((entry) => (
        Number(entry.id) === Number(updatedUser?.id)
          ? { ...entry, ...updatedUser }
          : entry
      )));

      if (Number(selectedUserId) === Number(updatedUser?.id)) {
        await refreshDetail(updatedUser.id);
      }

      setNoticeMessage(`${targetUser.username} is now ${nextRole}.`);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.message || "Could not update user role.");
    } finally {
      setPendingRoleUserId(null);
    }
  };

  const handleCollectorTierChange = async (targetUser, nextCollectorTier) => {
    if (!nextCollectorTier || nextCollectorTier === targetUser.collectorTier || targetUser.role !== "collector") {
      return;
    }

    setPendingCollectorTierUserId(targetUser.id);
    setNoticeMessage("");

    try {
      const payload = await updateAdminUserCollectorTier(targetUser.id, nextCollectorTier);
      const updatedUser = payload?.user;

      setUsers((currentUsers) => currentUsers.map((entry) => (
        Number(entry.id) === Number(updatedUser?.id)
          ? { ...entry, ...updatedUser }
          : entry
      )));

      if (Number(selectedUserId) === Number(updatedUser?.id)) {
        await refreshDetail(updatedUser.id);
      }

      setNoticeMessage(`${targetUser.username} moved to ${getCollectorTierDefinition(nextCollectorTier).label}.`);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.message || "Could not update user collector tier.");
    } finally {
      setPendingCollectorTierUserId(null);
    }
  };

  const renderAuditMessage = (entry) => {
    if (entry.actionType === "role_changed") {
      return `Role changed from ${entry.oldValue || "unknown"} to ${entry.newValue || "unknown"}`;
    }

    if (entry.actionType === "collector_tier_changed") {
      return `Collector tier changed from ${entry.oldValue || "unknown"} to ${entry.newValue || "unknown"}`;
    }

    if (entry.actionType === "status_changed") {
      return `Status changed from ${entry.oldValue || "unknown"} to ${entry.newValue || "unknown"}`;
    }

    return entry.actionType;
  };

  const rangeStart = users.length > 0 ? ((pagination.page - 1) * pagination.pageSize) + 1 : 0;
  const rangeEnd = users.length > 0 ? Math.min(pagination.page * pagination.pageSize, pagination.totalItems) : 0;
  const visiblePageNumbers = getVisiblePageNumbers(pagination.page, pagination.totalPages);
  const tradeFeedbackEntries = Array.isArray(selectedUserDetail?.tradeFeedback) ? selectedUserDetail.tradeFeedback : [];
  const tradeFeedbackSummary = summarizeTradeFeedback(tradeFeedbackEntries);
  return (
    <div className="account-shell">
      <div className="account-page-header">
        <div>
          <h1 className="account-page-title">Admin Users</h1>
          <p className="account-copy">
            Monitor active sellers, completed trades and recent activity, then suspend accounts when abuse needs a fast response.
          </p>
        </div>
      </div>

      <section className="account-settings-panel admin-users-panel">
        <div className="account-settings-panel__header">
          <div>
            <h2 className="account-title">User overview</h2>
            <p className="account-copy">
              Search by username or email, then narrow the list by account status. Click a row to inspect the account in detail.
            </p>
          </div>
          <div className="admin-users-controls">
            <form className="admin-users-toolbar" onSubmit={handleSearchSubmit}>
              <input
                className="admin-users-search"
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search username or email"
                aria-label="Search users"
              />
              <button className="admin-users-action admin-users-action--secondary" type="submit" disabled={isRefreshing}>
                {isRefreshing ? "Searching..." : "Search"}
              </button>
            </form>
            <div className="admin-users-filter-row" role="tablist" aria-label="User status filters">
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  className={filter.value === statusFilter ? "admin-users-filter admin-users-filter--active" : "admin-users-filter"}
                  onClick={() => handleStatusFilterChange(filter.value)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <div className="admin-users-filter-row" role="tablist" aria-label="User role filters">
              {ROLE_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  className={filter.value === roleFilter ? "admin-users-filter admin-users-filter--active" : "admin-users-filter"}
                  onClick={() => handleRoleFilterChange(filter.value)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {noticeMessage ? <div className="account-notice">{noticeMessage}</div> : null}
        {errorMessage ? <div className="account-error">{errorMessage}</div> : null}
        {isLoading ? <div className="account-loading">Loading users...</div> : null}

        {!isLoading ? (
          <div className="admin-users-layout">
            <div className="admin-users-table-shell" ref={tableSectionRef}>
              <table className="admin-users-table">
                <thead>
                  <tr>
                    <th>
                      <button type="button" className="admin-users-sort-button" onClick={() => handleSortChange("username")}>
                        User <span>{getSortIndicator("username")}</span>
                      </button>
                    </th>
                    <th>
                      <button type="button" className="admin-users-sort-button" onClick={() => handleSortChange("activeListings")}>
                        Active listings <span>{getSortIndicator("activeListings")}</span>
                      </button>
                    </th>
                    <th>
                      <button type="button" className="admin-users-sort-button" onClick={() => handleSortChange("completedTradeCount")}>
                        Trades completed <span>{getSortIndicator("completedTradeCount")}</span>
                      </button>
                    </th>
                    <th>
                      <button type="button" className="admin-users-sort-button" onClick={() => handleSortChange("lastSeenAt")}>
                        Last active <span>{getSortIndicator("lastSeenAt")}</span>
                      </button>
                    </th>
                    <th>
                      <button type="button" className="admin-users-sort-button" onClick={() => handleSortChange("status")}>
                        Status <span>{getSortIndicator("status")}</span>
                      </button>
                    </th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length > 0 ? users.map((user) => (
                    <tr
                      key={user.id}
                      className={Number(selectedUserId) === Number(user.id) ? "admin-users-row admin-users-row--selected" : "admin-users-row"}
                      onClick={() => setSelectedUserId(user.id)}
                    >
                      <td>
                        <div className="admin-users-identity">
                          <div className="admin-users-identity-topline">
                            <strong style={{ display: "inline-flex", alignItems: "center", minWidth: 0 }}>
                              <UserIdentityLabel username={user.username} tier={user.collectorTier} role={user.role} size="sm" color="#0f172a" fontWeight={700} wrap truncateAt={10} />
                            </strong>
                            <span className={`admin-users-role-badge admin-users-role-badge--${user.role}`}>{user.role}</span>
                          </div>
                          <span>{user.email}</span>
                        </div>
                      </td>
                      <td>{Number(user.activeListings ?? 0)}</td>
                      <td>{Number(user.completedTradeCount ?? 0)}</td>
                      <td>{formatLastSeen(user.lastSeenAt)}</td>
                      <td>
                        <div className="admin-users-status-cell">
                          <span className={`admin-users-status-badge admin-users-status-badge--${user.status}`}>
                            {user.status}
                          </span>
                          {user.suspensionReason ? (
                            <span
                              className="admin-users-reason-indicator"
                              title={user.suspensionReason}
                              aria-label={`Suspension reason: ${user.suspensionReason}`}
                            >
                              !
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={user.status === "suspended" ? "admin-users-action admin-users-action--secondary" : "admin-users-action admin-users-action--danger"}
                          disabled={pendingUserId === user.id}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleStatusToggle(user);
                          }}
                        >
                          {pendingUserId === user.id
                            ? "Saving..."
                            : user.status === "suspended"
                              ? "Reactivate"
                              : "Suspend"}
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="6">
                        <div className="admin-users-empty">No users matched the current filters.</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="admin-users-pagination">
                <div className="admin-users-pagination-summary">
                  Showing {rangeStart}-{rangeEnd} of {pagination.totalItems}
                </div>
                <div className="admin-users-pagination-controls">
                  <label className="admin-users-page-size-label">
                    Per page
                    <select className="admin-users-page-size-select" value={pagination.pageSize} onChange={handlePageSizeChange}>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    className="admin-users-action admin-users-action--secondary"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    aria-label="Previous page"
                    title="Previous page"
                    disabled={!pagination.hasPreviousPage || isRefreshing}
                  >
                    <span className="app-pagination__icon" aria-hidden="true">{"<"}</span>
                  </button>
                  <div className="admin-users-page-number-list" aria-label="Admin users pages">
                    {visiblePageNumbers.map((pageNumber) => {
                      if (typeof pageNumber !== "number") {
                        return (
                          <span key={pageNumber} className="admin-users-page-ellipsis" aria-hidden="true">
                            ...
                          </span>
                        );
                      }

                      const isActive = pageNumber === pagination.page;

                      return (
                        <button
                          key={pageNumber}
                          type="button"
                          className={isActive ? "admin-users-page-button admin-users-page-button--active" : "admin-users-page-button"}
                          onClick={() => handlePageChange(pageNumber)}
                          aria-current={isActive ? "page" : undefined}
                          disabled={isRefreshing}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>
                  <label className="app-pagination__jump">
                    Page
                    <div className="app-pagination__jump-controls">
                      <div className="app-pagination__select-shell">
                        <input
                          type="number"
                          min="1"
                          max={String(pagination.totalPages)}
                          step="1"
                          inputMode="numeric"
                          aria-label="Jump to page"
                          className="app-pagination__page-input"
                          value={pageInput}
                          disabled={isRefreshing}
                          onChange={(event) => setPageInput(event.target.value)}
                          onBlur={commitPageInput}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              commitPageInput();
                            }

                            if (event.key === "Escape") {
                              event.preventDefault();
                              setPageInput(String(pagination.page));
                              event.currentTarget.blur();
                            }
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        className="app-pagination__button app-pagination__button--text app-pagination__jump-button"
                        onClick={commitPageInput}
                        disabled={isRefreshing}
                      >
                        Go
                      </button>
                    </div>
                  </label>
                  <button
                    type="button"
                    className="admin-users-action admin-users-action--secondary"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    aria-label="Next page"
                    title="Next page"
                    disabled={!pagination.hasNextPage || isRefreshing}
                  >
                    <span className="app-pagination__icon" aria-hidden="true">{">"}</span>
                  </button>
                </div>
              </div>
            </div>

            <aside className="admin-users-detail-panel">
              {isDetailLoading ? <div className="account-loading">Loading user detail...</div> : null}
              {!isDetailLoading && selectedUserDetail?.user ? (
                <>
                  <div className="admin-users-detail-header">
                    <div className="admin-users-detail-header-copy">
                      <div className="admin-users-detail-eyebrow">Selected account</div>
                      <h3 className="admin-users-detail-title">
                        <CollectorTierBadge tier={selectedUserDetail.user.collectorTier} role={selectedUserDetail.user.role} size="md" showLabel />
                        <span className="admin-users-detail-username">{selectedUserDetail.user.username}</span>
                      </h3>
                      <div className="admin-users-detail-email">{selectedUserDetail.user.email}</div>
                    </div>
                    <span className={`admin-users-status-badge admin-users-status-badge--${selectedUserDetail.user.status}`}>
                      {selectedUserDetail.user.status}
                    </span>
                  </div>

                  <div className="admin-users-detail-grid">
                    <div className="admin-users-detail-stat">
                      <span className="admin-users-detail-label">Role</span>
                      <strong>{selectedUserDetail.user.role}</strong>
                      {canEditRoles ? (
                        <div className="admin-users-role-actions">
                          {ROLE_FILTERS.filter((entry) => entry.value !== "all").map((entry) => (
                            <button
                              key={entry.value}
                              type="button"
                              className={entry.value === selectedUserDetail.user.role ? "admin-users-role-chip admin-users-role-chip--active" : "admin-users-role-chip"}
                              disabled={pendingRoleUserId === selectedUserDetail.user.id || Number(currentUser?.id) === Number(selectedUserDetail.user.id)}
                              onClick={() => handleRoleChange(selectedUserDetail.user, entry.value)}
                            >
                              {pendingRoleUserId === selectedUserDetail.user.id && entry.value === selectedUserDetail.user.role ? "Saving..." : entry.label}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className="admin-users-detail-stat">
                      <span className="admin-users-detail-label">Collector tier</span>
                      <strong>
                        {selectedUserDetail.user.role === "collector"
                          ? getCollectorTierDefinition(selectedUserDetail.user.collectorTier).label
                          : "Not used"}
                      </strong>
                      {selectedUserDetail.user.role === "collector" && canEditRoles ? (
                        <div className="admin-users-role-actions">
                          {COLLECTOR_TIER_OPTIONS.map((entry) => (
                            <button
                              key={entry.value}
                              type="button"
                              className={entry.value === selectedUserDetail.user.collectorTier ? "admin-users-role-chip admin-users-role-chip--active" : "admin-users-role-chip"}
                              disabled={pendingCollectorTierUserId === selectedUserDetail.user.id}
                              onClick={() => handleCollectorTierChange(selectedUserDetail.user, entry.value)}
                              title={`${entry.label} · ${entry.description}`}
                            >
                              {pendingCollectorTierUserId === selectedUserDetail.user.id && entry.value === selectedUserDetail.user.collectorTier
                                ? "Saving..."
                                : getCollectorTierDefinition(entry.value).shortLabel}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className="admin-users-detail-stat">
                      <span className="admin-users-detail-label">Active listings</span>
                      <strong>{Number(selectedUserDetail.summary?.activeListings ?? 0)}</strong>
                    </div>
                    <div className="admin-users-detail-stat">
                      <span className="admin-users-detail-label">Trades completed</span>
                      <strong>{Number(selectedUserDetail.user.completedTradeCount ?? 0)}</strong>
                    </div>
                    <div className="admin-users-detail-stat">
                      <span className="admin-users-detail-label">Want items</span>
                      <strong>{Number(selectedUserDetail.summary?.wantItems ?? 0)}</strong>
                    </div>
                    <div className="admin-users-detail-stat">
                      <span className="admin-users-detail-label">Inventory items</span>
                      <strong>{Number(selectedUserDetail.summary?.inventoryItems ?? 0)}</strong>
                    </div>
                  </div>

                  <div className="admin-users-detail-meta">
                    <div><strong>Last active:</strong> {formatLastSeen(selectedUserDetail.user.lastSeenAt)}</div>
                    <div><strong>Created:</strong> {formatDateTime(selectedUserDetail.user.createdAt)}</div>
                    <div><strong>Terms accepted:</strong> {formatAcceptance(selectedUserDetail.user.termsAcceptedAt, selectedUserDetail.user.termsVersion)}</div>
                    <div><strong>Privacy accepted:</strong> {formatAcceptance(selectedUserDetail.user.privacyAcceptedAt, selectedUserDetail.user.privacyVersion)}</div>
                    <div><strong>Email verified:</strong> {formatDateTime(selectedUserDetail.user.emailVerifiedAt)}</div>
                    <div><strong>Suspended at:</strong> {formatDateTime(selectedUserDetail.user.suspendedAt)}</div>
                    {selectedUserDetail.user.role === "collector" ? (
                      <div>
                        <strong>Tier limits:</strong> {formatCollectorTierLimit(getCollectorTierDefinition(selectedUserDetail.user.collectorTier).myTradersLimit)} My Traders / {formatCollectorTierLimit(getCollectorTierDefinition(selectedUserDetail.user.collectorTier).myWantsLimit)} My Wants
                      </div>
                    ) : null}
                  </div>

                  {selectedUserDetail.user.suspensionReason ? (
                    <div className="admin-users-detail-reason">
                      <div className="admin-users-detail-label">Suspension reason</div>
                      <p>{selectedUserDetail.user.suspensionReason}</p>
                    </div>
                  ) : null}

                  <div className="admin-users-audit-log">
                    <div className="admin-users-detail-label">Trade feedback</div>
                    {tradeFeedbackEntries.length > 0 ? (
                      <>
                        <div className="admin-users-feedback-summary">
                          <button
                            type="button"
                            className="admin-users-feedback-summary-card admin-users-feedback-summary-button"
                            onClick={() => setIsTradeFeedbackModalOpen(true)}
                          >
                            <span className="admin-users-detail-label">Total reviews</span>
                            <strong>{tradeFeedbackEntries.length}</strong>
                          </button>
                          <div className="admin-users-feedback-summary-card admin-users-feedback-summary-card--emoji" aria-label={`${tradeFeedbackSummary.happy} good reviews`}>
                            <strong>
                              <span className="admin-users-feedback-emoji" role="img" aria-hidden="true">😀</span>
                              <span>{tradeFeedbackSummary.happy}</span>
                            </strong>
                          </div>
                          <div className="admin-users-feedback-summary-card admin-users-feedback-summary-card--emoji" aria-label={`${tradeFeedbackSummary.neutral} neutral reviews`}>
                            <strong>
                              <span className="admin-users-feedback-emoji" role="img" aria-hidden="true">😐</span>
                              <span>{tradeFeedbackSummary.neutral}</span>
                            </strong>
                          </div>
                          <div className="admin-users-feedback-summary-card admin-users-feedback-summary-card--emoji" aria-label={`${tradeFeedbackSummary.unhappy} bad reviews`}>
                            <strong>
                              <span className="admin-users-feedback-emoji" role="img" aria-hidden="true">🙁</span>
                              <span>{tradeFeedbackSummary.unhappy}</span>
                            </strong>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="admin-users-empty">No trade feedback recorded for this user yet.</div>
                    )}
                  </div>

                  <TradeFeedbackLogModal
                    isOpen={isTradeFeedbackModalOpen}
                    onClose={() => setIsTradeFeedbackModalOpen(false)}
                    user={selectedUserDetail.user}
                    entries={tradeFeedbackEntries}
                  />

                  <div className="admin-users-audit-log">
                    <div className="admin-users-detail-label">Recent admin activity</div>
                    {Array.isArray(selectedUserDetail.auditLogs) && selectedUserDetail.auditLogs.length > 0 ? (
                      <div className="admin-users-audit-list">
                        {selectedUserDetail.auditLogs.map((entry) => (
                          <div key={entry.id} className="admin-users-audit-item">
                            <div className="admin-users-audit-row">
                              <strong>{renderAuditMessage(entry)}</strong>
                              <span>{formatDateTime(entry.createdAt)}</span>
                            </div>
                            <div className="admin-users-audit-meta">
                              <span>
                                by {entry.actor?.username || "Unknown admin"}
                                {entry.actor?.role ? ` (${entry.actor.role})` : ""}
                              </span>
                              {entry.note ? <span>Reason: {entry.note}</span> : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="admin-users-empty">No admin actions recorded for this user yet.</div>
                    )}
                  </div>
                </>
              ) : null}

              {!isDetailLoading && !selectedUserDetail?.user ? (
                <div className="admin-users-empty">Select a user to inspect account details.</div>
              ) : null}
            </aside>
          </div>
        ) : null}
      </section>
    </div>
  );
}